import React, { useEffect, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../../APIs";
import { myLocalStorage } from "../../../../components/StorageHelper";
import { GrServerCluster } from "react-icons/gr";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";

const BLUE = "#3b83f6";

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const ClustersListSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
    <div className="role-panel !min-h-0 !max-h-none">
      <div className="role-panel-header">
        <div>
          <Sk className="h-4 w-24 rounded" />
          <Sk className="h-3 w-56 rounded mt-2" />
        </div>
        <Sk className="h-6 w-9 rounded-full" />
      </div>
      <div className="role-search-shell">
        <Sk className="h-10 w-full rounded-xl" />
      </div>
      <div className="role-list">
        {Array.from({ length: 6 }).map((_, idx) => (
          <div
            key={idx}
            className="role-row"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <Sk className="h-4 w-1/3 rounded" />
                <Sk className="h-3 w-1/4 rounded mt-2" />
              </div>
            </div>
            <Sk className="h-4 w-24 rounded" />
            <Sk className="h-4 w-24 rounded ml-4" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const RolesListSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
    <div className="cloud-card-surface rounded-2xl p-3">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <Sk className="h-10 w-10 rounded-xl" />
          <div>
            <Sk className="h-5 w-48 rounded" />
            <Sk className="h-3 w-28 rounded mt-2" />
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <Sk className="h-9 w-16 rounded-lg" />
          <Sk className="h-9 w-20 rounded-lg" />
          <Sk className="h-9 w-24 rounded-lg" />
        </div>
      </div>
    </div>

    <div className="role-dashboard-grid">
      <div className="role-panel">
        <div className="role-panel-header">
          <div>
            <Sk className="h-4 w-32 rounded" />
            <Sk className="h-3 w-44 rounded mt-2" />
          </div>
          <Sk className="h-6 w-9 rounded-full" />
        </div>

        <div className="role-search-shell">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="role-list">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="role-row"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-10 w-10 rounded-xl flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="role-panel">
        <div className="role-detail-header">
          <div className="flex items-center gap-3">
            <Sk className="h-10 w-10 rounded-xl" />
            <div className="min-w-0 flex-1">
              <Sk className="h-5 w-2/3 rounded" />
              <Sk className="h-4 w-1/3 rounded mt-2" />
            </div>
          </div>
        </div>

        <div className="role-detail-tabs">
            <Sk className="h-4 w-16 rounded" />
            <Sk className="h-4 w-24 rounded" />
        </div>

        <div className="role-detail-content role-info-stack">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className="role-info-card">
              <Sk className="h-3 w-24 rounded" />
              <Sk className="h-4 w-full rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const K8Roles = ({
  selectedTenantName,
  allapi,
  rolesApi,
  cloud,
  rolespoliciesApi,
}) => {
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [k8Roles, setK8Roles] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleRules, setSelectedRoleRules] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [searchCluster, setSearchCluster] = useState("");
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [isRulesLoading, setIsRulesLoading] = useState(false);

  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

  const handleClusterSearchChange = (event) => {
    setSearchCluster(event.target.value);
  };

  const fetchAllK8Resources = async () => {
    const response = await axios.get(`${NGROK}${allapi}`);
    if (response.data) {
      if (cloud === "AWS") {
        setClusters(response.data.clusters);
      } else setClusters(response.data);
    }
    setLoading(false);
  };

  const getAllK8Roles = async (filterType, clusterId) => {
    const response = await axios.get(
      `${NGROK}${rolesApi}?clusterId=${clusterId}&tenantName=${latestTenant}&cloud=${cloud}&level=${filterType.toUpperCase()}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const fetchData = async (initialLoad = false, filterType, clusterId) => {
    try {
      const [roles] = await Promise.all([getAllK8Roles(filterType, clusterId)]);
      setK8Roles(roles);
      setLoading(false);
      if (initialLoad && roles.length > 0) {
        setSelectedRole(roles[0]);
        getRules(roles[0]);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    try {
      setSelectedRole(null);
      setSelectedRoleRules([]);
      let clusterId = selectedCluster?.azureId;
      if (cloud === "AWS") clusterId = selectedCluster.clusterArn;
      else if (cloud === "GCP") clusterId = selectedCluster?.gcpClusterId;
      fetchData(true, newFilter, clusterId);
      setFilter(newFilter);
    } catch (e) {
      console.log(e);
    }
  };

  const handleRoleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    // Search is already reactive via state
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const filteredRoles = k8Roles?.filter(
    (role) =>
      role?.name && role?.name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredClusters = clusters.filter((resource) =>
    cloud === "AWS"
      ? resource.clusterName.toLowerCase().includes(searchCluster.toLowerCase())
      : resource.name.toLowerCase().includes(searchCluster.toLowerCase())
  );

  const getRules = async (role) => {
    setIsRulesLoading(true);
    try {
      const response = await axios.get(
        `${NGROK}${rolespoliciesApi}?uid=${role?.uid}&tenantName=${latestTenant}&cloud=${role?.cloudType}&clusterId=${role?.clusterId}`
      );
      if (response.status === 200) {
        if (response?.data) setSelectedRoleRules(response.data);
        else setSelectedRoleRules([]);
      } else setSelectedRoleRules([]);
    } catch (error) {
      setSelectedRoleRules([]);
    }
    setIsRulesLoading(false);
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedDetailsTab(0);
    getRules(role);
  };

  const handlePublishToggle = async (event, role, cluster, uid) => {
    console.log(role);
    const published = event.target.checked;
    setK8Roles((prevData) =>
      prevData.map((r) =>
        r.uid === role.uid ? { ...r, published } : r
      )
    );
    let requestData = {
      resourceId: role?.uid,
      wsTenantName: selectedTenantName,
      flag: event.target.checked,
      type: "NAMESPACE_ROLE",
      cloudProviderType: cloud,
      clusterId:
        cloud === "AWS"
          ? selectedCluster.clusterArn
          : cloud === "GCP"
            ? selectedCluster?.gcpClusterId
            : selectedCluster?.azureId,
      resourceAccountId: role.resourceAccountId,
    };

    await axios.patch(
      `${NGROK}/api/k8-resources/v1/publish`,
      requestData
    );
  };

  useEffect(() => {
    fetchAllK8Resources();
  }, [latestTenant]);

  useEffect(() => {
    if (selectedCluster) {
      setLoading(true);
      handleFilterChange("all");
    }
  }, [selectedCluster]);

  const getRoleType = (role) => {
    if (role?.level === "CLUSTER") return "Cluster Role";
    if (role?.level === "NAMESPACE") return "Namespace Role";
    return "Role";
  };

  const selectedKey = selectedRole?.uid;

  const renderRulesContent = () => {
    if (isRulesLoading) {
      return (
        <div className="flex justify-center py-8">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: BLUE, borderTopColor: "transparent" }}
          />
        </div>
      );
    }

    const rulesArray = Array.isArray(selectedRoleRules) ? selectedRoleRules : [selectedRoleRules];

    if (!selectedRoleRules || rulesArray.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
          <p className="text-sm mt-2">No rules available</p>
        </div>
      );
    }

    return (
      <div className="role-info-stack">
        {rulesArray.map((rule, ruleIndex) => (
          <div key={ruleIndex} className="role-info-card">
            <div className="role-info-label">Rule {ruleIndex + 1}</div>

            {rule?.clusterRoleUID && (
              <div className="mt-3">
                <div className="role-info-label">Cluster Role UID</div>
                <p className="role-info-value font-mono">
                  {rule.clusterRoleUID}
                </p>
              </div>
            )}

            {rule?.namespaceRoleUID && (
              <div className="mt-3">
                <div className="role-info-label">Namespace Role UID</div>
                <p className="role-info-value font-mono">
                  {rule.namespaceRoleUID}
                </p>
              </div>
            )}

            {rule?.verbs && rule.verbs.length > 0 && (
              <div className="mt-3">
                <div className="role-info-label">Verbs</div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {rule.verbs.map((verb, i) => (
                    <span
                      key={i}
                      className="role-type-badge"
                    >
                      {verb}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {rule?.apiGroups && rule.apiGroups.length > 0 && (
              <div className="mt-3">
                <div className="role-info-label">API Groups</div>
                <p className="role-info-value">
                  {rule.apiGroups.join(", ") || "core"}
                </p>
              </div>
            )}

            {rule?.resources && rule.resources.length > 0 && (
              <div className="mt-3">
                <div className="role-info-label">Resources</div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {rule.resources.map((res, i) => (
                    <span
                      key={i}
                      className="role-type-badge"
                    >
                      {res}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {rule?.nonResourceURLs && rule.nonResourceURLs.length > 0 && (
              <div className="mt-3">
                <div className="role-info-label">Non Resource URLs</div>
                <p className="role-info-value">
                  {rule.nonResourceURLs.join(", ")}
                </p>
              </div>
            )}

            {rule?.resourceNames && rule.resourceNames.length > 0 && (
              <div className="mt-3">
                <div className="role-info-label">Resource Names</div>
                <p className="role-info-value">
                  {rule.resourceNames.join(", ")}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Cluster Selection View
  if (selectedCluster === null) {
    if (loading) return <ClustersListSkeleton />;

    return (
      <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
        <div className="role-panel !min-h-0 !max-h-none">
          <div className="role-panel-header">
            <div>
              <div className="role-panel-title">Clusters</div>
              <div className="role-panel-subtitle">Select a Kubernetes cluster to inspect roles</div>
            </div>
            <span className="role-count-pill">{filteredClusters.length}</span>
          </div>

          <div className="role-search-shell">
            <div className="relative">
              <input
                type="text"
                placeholder="Search clusters..."
                value={searchCluster}
                onChange={handleClusterSearchChange}
                className="role-search-input"
              />
              <SearchIcon
                fontSize="small"
                className="role-search-icon"
              />
            </div>
          </div>

          <div className="role-list !max-h-[560px]">
            {filteredClusters.length > 0 ? (
              filteredClusters.map((resource, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedCluster(resource)}
                  className="role-row"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="role-avatar">
                      <GrServerCluster className="w-5 h-5" style={{ color: BLUE }} />
                    </div>
                    <div className="role-row-text">
                      <p className="role-row-title">
                        {cloud === "AWS" ? resource.clusterName : resource.name}
                      </p>
                      <p className="role-row-subtitle">
                        Region: {resource.regionName || resource.location || resource.region || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {resource.resourceGroupName && (
                      <span className="role-type-badge">{resource.resourceGroupName}</span>
                    )}
                    {resource.type && (
                      <span className="role-type-badge">{resource.type}</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="role-empty-state">
                <GrServerCluster className="w-12 h-12 mb-2" style={{ color: "rgba(0,0,0,0.25)" }} />
                <p className="text-sm">No clusters found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Roles View (after selecting cluster)
  return (
    <>
      {loading ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col w-full gap-4">
          <div className="cloud-card-surface rounded-2xl p-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  onClick={() => {
                    setSelectedCluster(null);
                    setSelectedRole(null);
                    setSelectedRoleRules([]);
                  }}
                  className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                  type="button"
                >
                  <ArrowBackIcon fontSize="small" className="text-muted-foreground" />
                </button>
                <div className="min-w-0">
                  <div className="role-panel-title truncate">{selectedCluster.name || selectedCluster.clusterName}</div>
                  <div className="role-panel-subtitle">
                    {selectedCluster.regionName || selectedCluster.location || selectedCluster.region || "Kubernetes cluster"}
                  </div>
                </div>
              </div>
              <div className="cs-tabs !m-0 !ml-auto !w-auto !bg-transparent flex-wrap justify-end">
                {[
                  { key: "all", label: "All" },
                  { key: "Cluster", label: "Cluster" },
                  { key: "NameSpace", label: "Namespace" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleFilterChange(tab.key)}
                    className={`cs-tab ${filter === tab.key ? "active" : ""}`}
                    type="button"
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="role-dashboard-grid">
            {/* Left Column - Roles List */}
            <div className="role-panel">
              <div className="role-panel-header">
                <div>
                  <div className="role-panel-title">Kubernetes Roles</div>
                  <div className="role-panel-subtitle">Select a role to inspect rules</div>
                </div>
                <span className="role-count-pill">{filteredRoles?.length || 0}</span>
              </div>

              <div className="role-search-shell">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="role-search-input"
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="role-search-icon"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="role-list">
                {filteredRoles?.length > 0 ? (
                  filteredRoles.map((role, index) => {
                    const roleKey = role.uid || index;
                    const isSelected = roleKey === selectedKey;

                    return (
                      <div
                        key={roleKey}
                        onClick={() => handleRoleClick(role)}
                        className={`role-row ${isSelected ? "active" : ""}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="role-avatar">
                            <SecurityIcon sx={{ fontSize: 18, color: BLUE }} />
                          </div>
                          <div className="role-row-text">
                            <p className="role-row-title">
                              {role.name}
                            </p>
                            <p className="role-row-subtitle">
                              {getRoleType(role)}
                            </p>
                          </div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <TailwindSwitch
                            isDisabled={false}
                            action={(e, data) => handlePublishToggle(e, data)}
                            data={role}
                            isChecked={role.published}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="role-empty-state">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Details Panel */}
            <div className="role-panel">
              {selectedRole ? (
                <>
                  {/* Role Header */}
                  <div className="role-detail-header">
                    <div className="role-avatar">
                      <SecurityIcon sx={{ fontSize: 20, color: BLUE }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="role-detail-title">{selectedRole?.name}</h3>
                      <p className="role-detail-subtitle">{getRoleType(selectedRole)}</p>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="role-detail-tabs">
                    <button
                      onClick={() => setSelectedDetailsTab(0)}
                      className={`role-detail-tab ${selectedDetailsTab === 0 ? "active" : ""}`}
                      type="button"
                    >
                      <InfoOutlinedIcon fontSize="small" />
                      Info
                    </button>
                    <button
                      onClick={() => setSelectedDetailsTab(1)}
                      className={`role-detail-tab ${selectedDetailsTab === 1 ? "active" : ""}`}
                      type="button"
                    >
                      <VpnKeyIcon fontSize="small" />
                      Role Rules
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="role-detail-content">
                    {selectedDetailsTab === 0 && (
                      <div className="role-info-stack">
                        <div className="role-info-card">
                          <div className="role-info-label">Role Name</div>
                          <div className="role-info-value">
                            {selectedRole?.name || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">UID</div>
                          <div className="role-info-value font-mono">
                            {selectedRole?.uid || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Role Type</div>
                          <div className="role-info-value">
                            {getRoleType(selectedRole)}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Cluster ID</div>
                          <div className="role-info-value font-mono">
                            {selectedRole?.clusterId || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Cloud Type</div>
                          <div className="role-info-value">
                            {selectedRole?.cloudType || cloud || "—"}
                          </div>
                        </div>

                        <div className="role-info-card">
                          <div className="role-info-label">Published</div>
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs rounded-full ${selectedRole?.published
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                              : "bg-gray-100 dark:bg-gray-800 text-muted-foreground"
                              }`}
                          >
                            {selectedRole?.published ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && renderRulesContent()}
                  </div>
                </>
              ) : (
                <div className="role-empty-state">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default K8Roles;
